/**
 * Pinned conversations live in this browser only (user decision 28.09.2026): a set of
 * conversation ids in localStorage, no server field. Pinned rows sort to the top of their
 * section, newest first, then the rest as before.
 */
import { useSyncExternalStore } from 'react';

export const PINNED_STORAGE_KEY = 'tri.chat.pinned';

let cache: ReadonlySet<string> | null = null;
const listeners = new Set<() => void>();

function load(): ReadonlySet<string> {
    try {
        const raw = window.localStorage.getItem(PINNED_STORAGE_KEY);
        const ids = raw ? (JSON.parse(raw) as unknown) : [];
        return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []);
    } catch {
        return new Set();
    }
}

function save(next: ReadonlySet<string>): void {
    try {
        window.localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(Array.from(next)));
    } catch {
        // storage blocked: the pin still holds for this session
    }
}

export function getPinnedConversations(): ReadonlySet<string> {
    if (!cache) cache = load();
    return cache;
}

export function isConversationPinned(id: unknown): boolean {
    return id != null && getPinnedConversations().has(String(id));
}

export function setConversationPinned(id: unknown, pinned: boolean): void {
    if (id == null) return;
    const next = new Set(getPinnedConversations());
    if (pinned) next.add(String(id));
    else next.delete(String(id));
    cache = next;
    save(next);
    listeners.forEach((listener) => listener());
}

export function toggleConversationPinned(id: unknown): boolean {
    const next = !isConversationPinned(id);
    setConversationPinned(id, next);
    return next;
}

/** Tests only. */
export function resetPinnedConversations(): void {
    cache = null;
    listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function usePinnedConversations(): ReadonlySet<string> {
    return useSyncExternalStore(subscribe, getPinnedConversations, getPinnedConversations);
}

/** Pinned first, each group newest first. Stable for equal timestamps. */
export function orderConversations<T>(
    list: readonly T[],
    pinned: ReadonlySet<string>,
    idOf: (item: T) => unknown,
    timestampOf: (item: T) => number | undefined,
): T[] {
    const rank = (item: T) => (pinned.has(String(idOf(item))) ? 0 : 1);
    return [...list].sort((a, b) => rank(a) - rank(b) || (timestampOf(b) ?? 0) - (timestampOf(a) ?? 0));
}
